import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IRegistrationView } from '../types';
import { answeredBy, sightingRole, unplacedFailure, validationErrors } from './registration-copy';
import { MessageList, StepHeading, slotMessage, useStepAction } from './step-parts';

/**
 * `confirm_identity` on a firm's or a team's run — "is this your firm?", "is this your team's lead
 * advisor?", asked about a number that matched somebody. A person's run asks on page 03 or 06
 * instead (`RecordStep`).
 *
 * **Two names can arrive, and the card shows the registry's where both do** (R4).
 * `registryName` is who the registry says holds the number; `displayName` is a participant we
 * already hold, sent on the linking branch alone — the one branch where naming somebody is the
 * point rather than a disclosure. They need not agree, and the registry's is the claim the
 * registrant is agreeing to: a yes to it is the half of a verification that survey-api cannot
 * make without them. Where neither came — an `ok` that named nobody the provider could read —
 * the screen says so rather than inventing one.
 *
 * **A team is asked about its lead advisor**, since the number is the lead's CRD and the name
 * the registry gives is a person's — FINRA's BrokerCheck's, or since survey-api's SR-396 the
 * SEC's IAPD's, so the lede names the pair (`answeredBy`). On the linking branch the two names
 * are two different things — the lead the registry names, and the team we hold under that lead's
 * number — so the team's is said in the linking sentence rather than left out.
 *
 * **Two answers and no third control.** "That is not me" is `/identity {confirmed: false}`,
 * which already is the step back: the run returns to the anchor with what the number
 * established dropped and its attempts kept. A separate Back would be the same request under
 * a second name (R7).
 *
 * **Where else the CRD stands is survey-api's to say** (SR-416's B5-11, RL §4): `sightings`, the
 * teams it leads and the team and firm rosters that name it — names, never an address, since the
 * number is public. A new TEAMS team is asked "Is this your team?" over the TEAMS sightings: a team
 * chosen is `sameTeam` on the yes, "None of these" is a yes with none, and survey-api decides what
 * either means (a team an account acts for ends the run refused; one none does takes the run to its
 * completion under that team's name). A 400 at `sameTeam` is said on the question. No sightings —
 * `[]`, or an API before SR-416 that sends no member — is the step as it was.
 */
export function ConfirmIdentityStep({
  view,
  onDecide
}: {
  view: IRegistrationView;
  onDecide: (confirmed: boolean, sameTeam?: string) => Promise<void>;
}) {
  const { pending, error, run } = useStepAction();
  const words = WORDS[view.survey ?? ''] ?? WORDS.person;
  const name = view.registryName ?? view.displayName;
  const sightings = view.sightings ?? [];
  // One radio per team: survey-api sends a sighting per participant and role, so a team its
  // CRD both leads and sits on the roster of comes twice.
  const teams =
    view.survey === 'TEAMS'
      ? sightings.filter(
          (sighting, index) =>
            sighting.survey === 'TEAMS' &&
            sightings.findIndex((other) => other.participantId === sighting.participantId) ===
              index
        )
      : [];
  // The answer to "Is this your team?" — a team's id, or `NEW_TEAM`; null until one is chosen.
  const [team, setTeam] = React.useState<Nullable<string>>(null);
  const problems = validationErrors(error);
  const sameTeamMessage = slotMessage(problems.sameTeam);
  const failure = unplacedFailure(error, SAME_TEAM_FIELDS, 'Could not record your answer.');

  return (
    <>
      <StepHeading title={words.title} lede={lede(view)} />

      <div className="mb-6 rounded-md border border-border bg-[var(--color-slight-blue)]/40 p-4">
        {name ? (
          <p className="text-lg text-[var(--color-royal-blue)]">{name}</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            The record gave no name we can show you.
          </p>
        )}
      </div>

      {view.displayName !== null && (
        // The linking branch: the number names a participant nobody can sign in as yet, so a
        // yes links the new account to it instead of starting an empty one beside it.
        <p className="mb-6 text-sm text-muted-foreground">
          {view.survey === 'TEAMS' ? (
            <>
              We already hold a team under this lead advisor’s number,{' '}
              <strong className="font-medium">{view.displayName}</strong>, and nobody has signed
              in for it yet. If it is your team, your new account will be linked to it.
            </>
          ) : (
            <>
              Nobody has signed in for this record yet. If it is {words.subject}, your new
              account will be linked to it.
            </>
          )}
        </p>
      )}

      {teams.length > 0 && (
        <fieldset className="mb-6 grid gap-2" aria-describedby="registration-same-team-hint">
          <legend className="mb-1 text-sm font-medium">Is this your team?</legend>
          <p id="registration-same-team-hint" className="mb-1 text-sm text-muted-foreground">
            This CRD number is already on these teams. Choose yours, or a new team.
          </p>
          {teams.map((sighting) => (
            <TeamChoice
              key={sighting.participantId}
              id={sighting.participantId}
              label={`${sighting.displayName} (${sightingRole(sighting.role)})`}
              team={team}
              onChoose={setTeam}
            />
          ))}
          <TeamChoice
            id={NEW_TEAM}
            label="None of these — a new team"
            team={team}
            onChoose={setTeam}
          />
          {sameTeamMessage && <p className="text-xs text-destructive">{sameTeamMessage}</p>}
          <MessageList messages={problems.sameTeam} />
        </fieldset>
      )}

      {failure && (
        <p role="alert" className="mb-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <div className="grid gap-3">
        <Button
          disabled={pending || (teams.length > 0 && team === null)}
          onClick={() =>
            void run(() => onDecide(true, team === null || team === NEW_TEAM ? undefined : team))
          }
        >
          {words.yes}
        </Button>
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => void run(() => onDecide(false))}
        >
          {words.no}
        </Button>
      </div>
    </>
  );
}

/** "None of these" on "Is this your team?" — no participant id spells it. */
const NEW_TEAM = 'new';

/** One answer to "Is this your team?". */
function TeamChoice({
  id,
  label,
  team,
  onChoose
}: {
  id: string;
  label: string;
  team: Nullable<string>;
  onChoose: (id: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="radio"
        name="registration-same-team"
        value={id}
        checked={team === id}
        onChange={() => onChoose(id)}
        className="size-4 accent-[var(--color-sapphire)]"
      />
      {label}
    </label>
  );
}

/** The one name a refusal of the identity answer is placed under. */
const SAME_TEAM_FIELDS: ReadonlySet<string> = new Set(['sameTeam']);

/** The question and its two answers, by who the number is about. */
const WORDS: Record<string, { title: string; subject: string; yes: string; no: string }> = {
  person: {
    title: 'Is this you?',
    subject: 'you',
    yes: 'Yes, that’s me',
    no: 'That is not me'
  },
  RIA_FIRM: {
    title: 'Is this your firm?',
    subject: 'your firm',
    yes: 'Yes, that is my firm',
    no: 'That is not my firm'
  },
  TEAMS: {
    title: 'Is this your team’s lead advisor?',
    subject: 'your team',
    yes: 'Yes, that is our lead advisor',
    no: 'That is not our lead advisor'
  }
};

/** Three sentences rather than one with holes in it: the registry's answer, our own record,
 *  and a match that came with no name are three different statements. */
function lede(view: IRegistrationView): string {
  if (view.registryName !== null) {
    return `That number is listed at ${answeredBy(view.survey ?? '')} under this name.`;
  }
  if (view.displayName !== null) return 'We already hold a record for that number.';
  return `That number matched a record at ${answeredBy(view.survey ?? '')}.`;
}
